"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidImportModal/PortfolioAccountsModal";

import { Icon } from "@ospweb/sdk-ui";
import { AddAccountRow } from "./AddAccountRow";
import { ConnectionsSection } from "./ConnectionsSection";
import { PlaidInstitutionRebindDialog } from "./PlaidInstitutionRebindDialog";
import type { PortfolioAccountsModalContentProps } from "./types";
import React from "react";
import { cn } from "utils";
import { hasAccountTargetCoverage } from "@/src/hooks/plaid/memberPlaidApi";
import { usePlaidConnectionsPanel } from "./usePlaidConnectionsPanel";

const I18n = I18nUtil.createScoped(Translations);

export const PortfolioAccountsModalContent: React.FC<
  PortfolioAccountsModalContentProps
> = ({
  onBindSuccess,
  onAddAccount,
  confirmEnabled,
  closeOnAddAccount,
  accountTarget,
  boundConnection,
  onClose,
  iconName,
  className,
  headerClassName,
  iconBtnClassName,
  safeAreaTop = true,
}) => {
  const {
    investConnections,
    nonInvestConnections,
    linkLoading,
    binding,
    webLinkHost,
    rebindUpdateLinkToken,
    handleRebindCancel,
    handleRebindAdd,
    onUpdateConnection,
  } = usePlaidConnectionsPanel({
    mode: "default",
    onBindSuccess,
    accountTarget,
    onClose,
    keepOpenOnBind: Boolean(confirmEnabled),
  });

  const actionDisabled = linkLoading || binding;
  const hasTargetCoverage = hasAccountTargetCoverage(
    investConnections,
    nonInvestConnections,
    accountTarget,
    boundConnection,
  );
  const confirmDisabled = actionDisabled || !hasTargetCoverage;
  // 关闭结果附带目标覆盖状态（仅 accountTarget 语境）：onboarding 编排层据此让
  // 无覆盖的关闭停留选题页而非进 loading（generate 只会 422 回选题）
  const closeWithCoverage = () =>
    onClose(
      accountTarget && !hasTargetCoverage
        ? { action: "close", withoutTargetCoverage: true }
        : { action: "close" },
    );

  return (
    <div
      className={cn(
        "relative flex min-h-0 w-full h-full flex-col md:gap-8 pb-safe md:pb-0",
        safeAreaTop && "pt-safe md:pt-0",
        className,
      )}
      data-testid="portfolio-accounts-modal-content"
    >
      {webLinkHost}
      {rebindUpdateLinkToken ? (
        <PlaidInstitutionRebindDialog
          onCancel={handleRebindCancel}
          onAdd={handleRebindAdd}
        />
      ) : null}
      <h2
        className={cn(
          "shrink-0 px-4 py-2 md:pt-4 title_xl text-content_primary md:px-0 md:pb-0 gap-3 flex flex-row items-center",
          headerClassName,
        )}
      >
        <button
          type="button"
          aria-label="Close"
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-full bg-background_solid_neutral_primary shadow-[0px_2px_20px_0px_rgba(0,0,0,0.08)]",
            iconBtnClassName,
          )}
          data-testid="portfolio-accounts-modal-close"
          data-openreplay-label="portfolio_accounts_close"
          onClick={closeWithCoverage}
        >
          <Icon
            name={iconName || "close"}
            size={24}
            className="text-content_primary"
          />
        </button>
        {I18n.t("My accounts")}
      </h2>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 md:p-0">
        {/* 恒双分区（不做 accountTarget 过滤）；accountTarget 只保留在上方
            hasTargetCoverage 门控与 Plaid 连接弹窗的账户类型筛选 */}
        <ConnectionsSection
          investConnections={investConnections}
          nonInvestConnections={nonInvestConnections}
          disabled={actionDisabled}
          onManage={onUpdateConnection}
          onNavigateAway={() => onClose({ action: "close" })}
        />
      </div>
      <div className="flex shrink-0 flex-col gap-3 px-4 pt-4 pb-6 md:p-0">
        <AddAccountRow
          onClick={() => {
            if (closeOnAddAccount) {
              onClose({ action: "add_account" });
              return;
            }
            onAddAccount?.();
          }}
        />
        {/* EDG-393 · bind ≠ Confirm：绑定成功后经 My accounts 的用户确认
            （V3.0.5 公共 Modal 可选底钮），才驱动 Post-link Ed 卡。 */}
        {confirmEnabled ? (
          <button
            type="button"
            className="button md_button primary_button button_m w-full"
            data-testid="portfolio-accounts-modal-confirm"
            data-openreplay-label="portfolio_accounts_confirm"
            disabled={confirmDisabled}
            onClick={() => onClose({ action: "confirm" })}
          >
            {I18n.t("Confirm")}
          </button>
        ) : null}
      </div>
    </div>
  );
};
